// Designs.js - Fixed, no horizontal scroll, table-layout fixed, optimized
import { useEffect, useState, useCallback } from "react";
import "./Designs.css";
import { UilSearch, UilTrashAlt, UilEdit } from "@iconscout/react-unicons";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DesignPreviewCanvas from "../../Components/DesignPreviewCanvas/DesignPreviewCanvas";

// â”€â”€â”€ Axios instance â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// No module-level interceptor — token is synced inside the component via
// useEffect, after Redux-Persist has rehydrated currentUser from localStorage.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000/api",
  timeout: 60000,
});

const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6"/>
  </svg>
);

const formatPreorderDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const Designs = () => {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [filteredDesigns, setFilteredDesigns] = useState([]);
  const [selectedColors, setSelectedColors] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(15);

  const { currentUser } = useSelector((state) => state.auth);

  // â”€â”€ Sync auth token to axios after Redux rehydration â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // currentUser shape: { message, worker: {...}, token: "Bearer eyJ...", refreshToken: "eyJ..." }
  useEffect(() => {
    const rawToken     = currentUser?.token || null;
    const refreshToken = currentUser?.refreshToken || null;

    if (rawToken) {
      const token = rawToken.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
      api.defaults.headers.common["Authorization"] = token;
      console.log("Designs: token synced to axios");
    } else {
      delete api.defaults.headers.common["Authorization"];
      console.warn("Designs: no token in currentUser");
    }

    if (refreshToken) {
      api.defaults.headers.common["x-refresh-token"] = refreshToken;
    } else {
      delete api.defaults.headers.common["x-refresh-token"];
    }
  }, [currentUser]);

  // Helper: Extract image URL from various formats
  const extractImageUrl = (img) => {
    if (!img) return null;
    if (typeof img === 'string') return img;
    if (img.url && typeof img.url === 'object' && img.url.url) return img.url.url;
    if (img.url && typeof img.url === 'string') return img.url;
    if (img.original && typeof img.original === 'string') return img.original;
    if (img.medium && typeof img.medium === 'string') return img.medium;
    if (img.thumbnail && typeof img.thumbnail === 'string') return img.thumbnail;
    return null;
  };

  const isFinishedProduct = (design) => design.isFinishedProduct === true;

  const findLinkedProduct = useCallback((productId) => {
    if (!productId) return null;
    const normalizedId = typeof productId === 'object'
      ? productId._id || productId.id
      : productId;
    let found = products.find(elt => elt._id === normalizedId);
    if (!found) found = products.find(elt => elt._id?.toString() === normalizedId?.toString());
    return found || (typeof productId === 'object' ? { ...productId, _id: normalizedId } : null);
  }, [products]);

  const getDesignPreorderInfo = useCallback((design) => {
    const linkedProduct = findLinkedProduct(design.linkedProduct);
    const directPreorder = design.isPreorder === true;
    const inheritedPreorder = !directPreorder && linkedProduct?.isPreorder === true;
    const preorderAvailableAt = directPreorder
      ? design.preorderAvailableAt
      : linkedProduct?.preorderAvailableAt;

    return {
      isPreorder: directPreorder || inheritedPreorder,
      inherited: inheritedPreorder,
      preorderAvailableAt: preorderAvailableAt || null,
    };
  }, [findLinkedProduct]);

  const getDesignAvailableColors = (design, product) => {
    if (!product) return [];
    if (design.availableColors && design.availableColors.length > 0) return design.availableColors;
    if (product.colorsAndSizes && typeof product.colorsAndSizes === 'object' && !Array.isArray(product.colorsAndSizes)) {
      return Object.keys(product.colorsAndSizes);
    }
    if (Array.isArray(product?.colorsAndSizes)) return product.colorsAndSizes.map(c => c.color);
    return [];
  };

  const getProductColorData = (product, colorName) => {
    if (!product || !product.colorsAndSizes || !colorName) return null;
    if (typeof product.colorsAndSizes === 'object' && !Array.isArray(product.colorsAndSizes)) {
      return product.colorsAndSizes[colorName] || null;
    }
    if (Array.isArray(product.colorsAndSizes)) {
      return product.colorsAndSizes.find(c => c.color === colorName) || null;
    }
    return null;
  };

  const isValidProductColor = (product, colorName) => getProductColorData(product, colorName) !== null;

  const getSizesForColor = (product, colorName) => {
    const colorData = getProductColorData(product, colorName);
    if (!colorData) return {};
    if (typeof colorData === 'object' && !Array.isArray(colorData)) return colorData;
    if (colorData.sizes) return colorData.sizes;
    return {};
  };

  const getProductImageUrl = (product, side = 'front') => {
    if (!product || !product.imgs || product.imgs.length === 0) return null;
    const image = product.imgs.find(img => img.type === side);
    return extractImageUrl(image);
  };

  const getDesignImageUrl = (design, side = 'front') => {
    if (!design.imgs || design.imgs.length === 0) return null;
    const image = design.imgs.find(img => img.type === side);
    return image ? extractImageUrl(image) : null;
  };

  const getDesignMetadata = (design, side) => {
    if (!design.designMetadata) return null;
    if (side === 'front' && design.designMetadata.front && design.designMetadata.front.length > 0) return design.designMetadata.front;
    if (side === 'back' && design.designMetadata.back && design.designMetadata.back.length > 0) return design.designMetadata.back;
    return null;
  };

  const formatSizesCompact = (sizes) => {
    if (!sizes || Object.keys(sizes).length === 0) return <div className="no-sizes-message">—</div>;
    const allSizes = Object.keys(sizes);
    const isChildSizes = allSizes.some(size => !isNaN(parseInt(size)) && !['XS','S','M','L','XL','XXL'].includes(size));
    let sortedSizes;
    if (isChildSizes) {
      sortedSizes = Object.entries(sizes).sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
    } else {
      const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
      sortedSizes = Object.entries(sizes).sort((a, b) => {
        const indexA = sizeOrder.indexOf(a[0]);
        const indexB = sizeOrder.indexOf(b[0]);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }
    return (
      <div className="sizes-compact">
        {sortedSizes.map(([size, quantity]) => {
          const displaySize = isChildSizes ? `${size} ans` : size;
          const hasStock = quantity > 0;
          return (
            <span key={size} className={`size-badge ${hasStock ? 'in-stock' : 'out-of-stock'}`}
              title={`${displaySize}: ${quantity} pièces`}>
              <span className="size-label">{displaySize}</span>
              <span className="size-qty">{quantity}</span>
            </span>
          );
        })}
      </div>
    );
  };

  // â”€â”€ Fetch designs with pagination â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!currentUser?.token) return; // wait for token
    const fetchDesigns = async () => {
      try {
        const res = await api.get(`/designs?page=${currentPage}&limit=${itemsPerPage}`);
        setDesigns(res.data.designs || []);
        setFilteredDesigns(res.data.designs || []);
        if (res.data.pagination) {
          setTotalPages(res.data.pagination.totalPages);
          setTotalItems(res.data.pagination.totalItems);
        }
      } catch (err) {
        console.error("Erreur lors du chargement des designs :", err);
        toast.error("Erreur lors du chargement des designs");
      }
    };
    fetchDesigns();
  }, [currentPage, itemsPerPage, currentUser]);

  // â”€â”€ Fetch all products for product details â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!currentUser?.token) return; // wait for token
    const fetchProducts = async () => {
      try {
        const res = await api.get("/products", { params: { limit: 1000 } });
        setProducts(res.data.products || []);
        setLoading(false);
      } catch (err) {
        console.error("Erreur lors du chargement des produits :", err);
        toast.error("Erreur lors du chargement des produits");
        setLoading(false);
      }
    };
    fetchProducts();
  }, [currentUser]);

  // â”€â”€ Initialize selected colors â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (designs.length > 0 && products.length > 0) {
      const initialColors = {};
      designs.forEach(design => {
        const linkedProduct = getLinkedProduct(design.linkedProduct);
        if (linkedProduct && !selectedColors[design._id]) {
          const availableColors = getDesignAvailableColors(design, linkedProduct);
          const firstValidColor = availableColors.find(color => isValidProductColor(linkedProduct, color));
          if (firstValidColor) initialColors[design._id] = firstValidColor;
        }
      });
      if (Object.keys(initialColors).length > 0) setSelectedColors(prev => ({ ...prev, ...initialColors }));
    }
  }, [designs, products]);

  // â”€â”€ Search â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    const searchLower = search.trim().toLowerCase();
    let nextDesigns = designs;

    if (searchLower) {
      nextDesigns = nextDesigns.filter((design) => (
        design._id?.toString().toLowerCase().includes(searchLower) ||
        design.title?.toLowerCase().includes(searchLower) ||
        design.linkedProduct?.toString().toLowerCase().includes(searchLower) ||
        design.price?.toString().toLowerCase().includes(searchLower)
      ));
    }

    if (availabilityFilter === 'preorder') {
      nextDesigns = nextDesigns.filter((design) => getDesignPreorderInfo(design).isPreorder);
    } else if (availabilityFilter === 'standard') {
      nextDesigns = nextDesigns.filter((design) => !getDesignPreorderInfo(design).isPreorder);
    }

    setFilteredDesigns(nextDesigns);
  }, [search, designs, availabilityFilter, getDesignPreorderInfo]);

  const handleDeleteDesign = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce modèle ?")) return;
    setDeletingIds(prev => new Set(prev).add(id));
    try {
      await api.delete(`/designs/${id}`);
      const res = await api.get(`/designs?page=${currentPage}&limit=${itemsPerPage}`);
      setDesigns(res.data.designs || []);
      setFilteredDesigns(res.data.designs || []);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages);
        setTotalItems(res.data.pagination.totalItems);
      }
      toast.success("Modèle supprimé avec succès");
    } catch (err) {
      console.error("Erreur suppression du design :", err);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeletingIds(prev => { const newSet = new Set(prev); newSet.delete(id); return newSet; });
    }
  };

  const handleEditDesign = (designId) => navigate(`/designs/edit/${designId}`);

  const getLinkedProduct = (productId) => {
    return findLinkedProduct(productId);
  };

  const handleColorSelect = (designId, colorName) => {
    setSelectedColors(prev => ({ ...prev, [designId]: colorName }));
  };

  const toggleDropdown = (designId) => setOpenDropdowns(prev => ({ ...prev, [designId]: !prev[designId] }));
  const closeDropdown = (designId) => setOpenDropdowns(prev => ({ ...prev, [designId]: false }));
  const handleImageError = (designId, side) => setImageLoadErrors(prev => ({ ...prev, [`${designId}-${side}`]: true }));

  const goToNextPage = () => { if (currentPage < totalPages) { setCurrentPage(currentPage + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const goToPrevPage = () => { if (currentPage > 1) { setCurrentPage(currentPage - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const goToPage = (pageNumber) => { setCurrentPage(pageNumber); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const preorderCount = designs.filter((design) => getDesignPreorderInfo(design).isPreorder).length;
  const standardCount = designs.length - preorderCount;
  const MAX_VISIBLE_COLORS = 5;

  if (loading) {
    return (
      <div className='Products'>
        <div className="loading-container"><div className="loading-spinner-custom"></div></div>
      </div>
    );
  }

  return (
    <div className='Products'>
      <ToastContainer position="top-right" />
      <h1 className="page-title">Modèles</h1>

      <div className="availability-filter-bar">
        <button
          type="button"
          className={`availability-filter-btn ${availabilityFilter === 'all' ? 'active' : ''}`}
          onClick={() => setAvailabilityFilter('all')}
        >
          Tous ({designs.length})
        </button>
        <button
          type="button"
          className={`availability-filter-btn preorder ${availabilityFilter === 'preorder' ? 'active' : ''}`}
          onClick={() => setAvailabilityFilter('preorder')}
        >
          Precommandes ({preorderCount})
        </button>
        <button
          type="button"
          className={`availability-filter-btn ${availabilityFilter === 'standard' ? 'active' : ''}`}
          onClick={() => setAvailabilityFilter('standard')}
        >
          Catalogue ({standardCount})
        </button>
      </div>

      <div className="search-parent">
        <div className="search-large">
          <input className="search-text-large" placeholder="Rechercher un modèle..." onChange={(e) => setSearch(e.target.value)} />
          <UilSearch className="search-icon" />
        </div>
      </div>

      <div className="Table">
        <TableContainer component={Paper} className="table-container">
          <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow className="table-header">
                <TableCell sx={{ width: '27%', fontWeight: 'bold', fontSize: '16px' }}>Modèle</TableCell>
                <TableCell sx={{ width: '12%', fontWeight: 'bold', fontSize: '16px', textAlign: 'center' }}>Titre</TableCell>
                <TableCell sx={{ width: '12%', fontWeight: 'bold', fontSize: '16px' }}>Produit</TableCell>
                <TableCell sx={{ width: '25%', fontWeight: 'bold', fontSize: '16px' }}>Couleurs et tailles</TableCell>
                <TableCell sx={{ width: '8%', fontWeight: 'bold', fontSize: '16px' }}>Prix</TableCell>
                <TableCell sx={{ width: '9%', fontWeight: 'bold', fontSize: '16px' }}>B2B</TableCell>
                <TableCell sx={{ width: '10%', fontWeight: 'bold', fontSize: '16px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDesigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" className="no-designs">
                    {availabilityFilter === 'preorder'
                      ? 'Aucun modele en precommande'
                      : search
                        ? 'Aucun modele trouve pour cette recherche'
                        : 'Aucun modele disponible'}
                  </TableCell>
                </TableRow>
              ) : (
              filteredDesigns.map((design, index) => {
                const linkedProduct = getLinkedProduct(design.linkedProduct);
                const preorderInfo = getDesignPreorderInfo(design);
                const preorderLabel = formatPreorderDate(preorderInfo.preorderAvailableAt);
                const isFinished = isFinishedProduct(design);
                const productFrontImage = getProductImageUrl(linkedProduct, 'front');
                const productBackImage = getProductImageUrl(linkedProduct, 'back');
                const designFrontImage = getDesignImageUrl(design, 'front');
                const designBackImage = getDesignImageUrl(design, 'back');
                const frontMetadata = getDesignMetadata(design, 'front');
                const backMetadata = getDesignMetadata(design, 'back');
                const isDeleting = deletingIds.has(design._id);
                const designAvailableColors = getDesignAvailableColors(design, linkedProduct);
                const validColors = designAvailableColors.filter(color => isValidProductColor(linkedProduct, color));
                const currentColor = selectedColors[design._id] || (validColors[0] || null);
                const currentSizes = getSizesForColor(linkedProduct, currentColor);
                const hasFrontError = imageLoadErrors[`${design._id}-front`];
                const hasBackError = imageLoadErrors[`${design._id}-back`];
                const frontPreviewKey = `${design._id}-front`;
                const backPreviewKey = `${design._id}-back`;

                return (
                  <TableRow key={design._id || index} className="design-row" sx={{ "&:last-child td, &:last-child th": { border: 0 }, opacity: isDeleting ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>
                    {/* IMAGES COLUMN */}
                    <TableCell align="left" className="design-cell">
                      {isFinished ? (
                        <div className="design-images-wrapper">
                          <div className="design-preview-container">
                            <div className="design-preview-label">PRODUIT FINI</div>
                            {productFrontImage ? (
                              <div className="design-preview-canvas">
                                <img src={productFrontImage} alt="Finished product front" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                              </div>
                            ) : <div className="image-fallback-large">Image non disponible</div>}
                          </div>
                          {productBackImage && (
                            <div className="design-preview-container">
                              <div className="design-preview-label">DOS</div>
                              <div className="design-preview-canvas"><img src={productBackImage} alt="Finished product back" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /></div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="design-images-wrapper">
                          <div className="design-preview-container">
                            <div className="design-preview-label">DEVANT</div>
                            {productFrontImage && designFrontImage && !hasFrontError ? (
                              <DesignPreviewCanvas
                                key={frontPreviewKey}
                                productImage={productFrontImage}
                                designImage={designFrontImage}
                                color={currentColor}
                                metadata={frontMetadata}
                                className="design-preview-canvas"
                                onError={() => handleImageError(design._id, 'front')}
                              />
                            ) : <div className="design-preview-canvas image-fallback-large">{hasFrontError ? 'Erreur image' : (!productFrontImage ? 'No product' : 'No design')}</div>}
                          </div>
                          <div className="design-preview-container">
                            <div className="design-preview-label">DOS</div>
                            {productBackImage && designBackImage && !hasBackError ? (
                              <DesignPreviewCanvas
                                key={backPreviewKey}
                                productImage={productBackImage}
                                designImage={designBackImage}
                                color={currentColor}
                                metadata={backMetadata}
                                className="design-preview-canvas"
                                onError={() => handleImageError(design._id, 'back')}
                              />
                            ) : <div className="design-preview-canvas image-fallback-large">{hasBackError ? 'Erreur image' : (!productBackImage ? 'No product' : 'No design')}</div>}
                          </div>
                        </div>
                      )}
                    </TableCell>

                    <TableCell align="center" className="product-ref-cell" sx={{ verticalAlign: 'middle !important' }}>
                      <span className="title-text">{isFinished && <span style={{ marginRight: '6px' }}>🏷️</span>}{design.title || 'N/A'}</span>
                      {isFinished && <div style={{ fontSize: '11px', color: '#28a745', marginTop: '4px', fontWeight: '500' }}>✓ Prêt à vendre</div>}
                    </TableCell>

                    <TableCell align="left" className="product-ref-cell">
                      <span className="product-ref-text">{design.linkedProduct || 'N/A'}</span>
                    </TableCell>

                    <TableCell align="left" className="colors-sizes-cell">
                      {isFinished ? (
                        <div>
                          <div className="sizes-compact-container">
                            <div className="sizes-header"><span className="sizes-title">Tailles</span></div>
                            {linkedProduct && Object.keys(linkedProduct.sizesOnly || {}).length > 0 ? (
                              <div className="sizes-compact">
                                {Object.entries(linkedProduct.sizesOnly).map(([size, qty]) => (
                                  <span key={size} className={`size-badge ${Number(qty) > 0 ? 'in-stock' : 'out-of-stock'}`} title={`${size}: ${qty} pièces`}>
                                    <span className="size-label">{size}</span><span className="size-qty">{qty}</span>
                                  </span>
                                ))}
                              </div>
                            ) : <div className="no-sizes-message">Stock non défini</div>}
                          </div>
                          <div className="field-note" style={{ marginTop: '8px', fontSize: '11px' }}>📦 Produit fini - vente directe</div>
                        </div>
                      ) : (
                        <>
                          <div className="sizes-compact-container">
                            <div className="sizes-header"><span className="sizes-title">Tailles {currentColor ? `(${currentColor})` : ''}</span></div>
                            {currentColor ? formatSizesCompact(currentSizes) : <div className="no-sizes-message">Sélectionnez une couleur</div>}
                          </div>
                          {validColors.length > 0 ? (
                            <div className="colors-compact-container">
                              <div className="colors-header">
                                <span className="colors-title">Couleurs disponibles ({validColors.length})</span>
                                <span className="field-note" style={{ fontSize: '9px', marginLeft: '8px' }}>(cliquez pour changer)</span>
                              </div>
                              <div className="colors-display">
                                {validColors.slice(0, MAX_VISIBLE_COLORS).map((colorName, idx) => {
                                  const isSelected = colorName === currentColor;
                                  return (
                                    <button key={`${design._id}-color-${idx}`} className={`color-dot ${isSelected ? 'selected' : ''}`} style={{ backgroundColor: colorName }} onClick={() => handleColorSelect(design._id, colorName)} title={`${colorName} - Cliquez pour voir le design dans cette couleur`} />
                                  );
                                })}
                                {validColors.length > MAX_VISIBLE_COLORS && (
                                  <div className="color-dropdown">
                                    <button className="color-dropdown-btn" onClick={() => toggleDropdown(design._id)} title="Plus de couleurs">+{validColors.length - MAX_VISIBLE_COLORS}</button>
                                    {openDropdowns[design._id] && (
                                      <div className="color-dropdown-menu" onMouseLeave={() => closeDropdown(design._id)}>
                                        {validColors.slice(MAX_VISIBLE_COLORS).map((colorName, idx) => (
                                          <button key={`${design._id}-hidden-${idx}`} className={`color-dropdown-item ${colorName === currentColor ? 'selected' : ''}`} style={{ backgroundColor: colorName }} onClick={() => { handleColorSelect(design._id, colorName); closeDropdown(design._id); }} title={`${colorName} - Cliquez pour voir le design dans cette couleur`} />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="field-note" style={{ marginTop: '8px', fontSize: '10px', color: '#666' }}>💡 Ce modèle est disponible en {validColors.length} couleur(s)</div>
                            </div>
                          ) : <div className="no-sizes-message" style={{ marginTop: '10px' }}>Aucune couleur sélectionnée pour ce modèle</div>}
                        </>
                      )}
                    </TableCell>

                    <TableCell align="left" className="design-cell">
                      <span className="price-text">€{design.price || '0'}</span>
                      {design.discount > 0 && <div style={{ fontSize: '11px', color: '#28a745' }}>-{design.discount}% (€{(design.price * (1 - design.discount / 100)).toFixed(2)})</div>}
                    </TableCell>

                    <TableCell align="left" className="availability-cell">
                      <div className={`availability-pill ${preorderInfo.isPreorder ? 'preorder' : 'standard'}`}>
                        {preorderInfo.isPreorder ? 'Precommande' : 'Catalogue'}
                      </div>
                      {preorderInfo.isPreorder && (
                        <div className="availability-date">
                          {preorderInfo.inherited ? 'Via produit lie' : 'Modele'}
                          {preorderLabel ? ` - dispo le ${preorderLabel}` : ''}
                        </div>
                      )}
                    </TableCell>

                    <TableCell align="left" className="action-cell">
                      <div className="action-buttons-group">
                        <button className="action-icon-btn btn-edit" onClick={() => handleEditDesign(design._id)} title="Modifier"><span className="btn-icon-inner"><UilEdit /></span></button>
                        <button className="action-icon-btn btn-delete" onClick={() => !isDeleting && handleDeleteDesign(design._id)} disabled={isDeleting} title="Supprimer"><span className="btn-icon-inner"><UilTrashAlt /></span></button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }))}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <button className="pagination-btn prev-btn" onClick={goToPrevPage} disabled={currentPage === 1}><ChevronLeftIcon /><span>Précédent</span></button>
            <div className="pagination-numbers">
              {getPageNumbers().map((page, index) => page === '...' ? <span key={`dots-${index}`} className="pagination-dots">...</span> : <button key={page} className={`pagination-number ${currentPage === page ? 'active' : ''}`} onClick={() => goToPage(page)}>{page}</button>)}
            </div>
            <button className="pagination-btn next-btn" onClick={goToNextPage} disabled={currentPage === totalPages}><span>Suivant</span><ChevronRightIcon /></button>
          </div>
        )}
        <div className="pagination-info">Affichage {filteredDesigns.length} sur {totalItems} modèles</div>
      </div>
    </div>
  );
};
